import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function BreakfastWizard({ suggestedItems = [], onClose, onComplete }) {
    const [inventory, setInventory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selections, setSelections] = useState({}); // { itemName: level }
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchInventory();
    }, []);

    const fetchInventory = async () => {
        setIsLoading(true);
        try {
            const authToken = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/inventory`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const data = await res.json();

            // Dinamik Envanter Tarayıcı Mantığı: 
            // 1. Backend'den gelenSuggestedItems öncelikli
            // 2. Kategori veya isim bazlı kahvaltılık tespiti
            const BREAKFAST_KEYWORDS = ['peynir', 'zeytin', 'yumurta', 'domates', 'salatalık', 'biber', 'bal', 'reçel', 'tereyağ', 'ekmek', 'simit', 'süt', 'sucuk', 'salam', 'pastırma', 'kaymak', 'hellim', 'omlet', 'mısır gevreği'];

            const breakfastItems = data.inventory.filter(item => {
                const name = item.ingredient_name.toLowerCase();
                const isSuggested = suggestedItems.some(s => s.toLowerCase() === name);
                const isMatch = BREAKFAST_KEYWORDS.some(k => name.includes(k));
                return isSuggested || isMatch;
            });

            // Gruplandırma ve Tekilleştirme (Envanterde birden fazla batch olabilir)
            const uniqueItems = [];
            breakfastItems.forEach(item => {
                if (!uniqueItems.find(u => u.name === item.ingredient_name)) {
                    uniqueItems.push({
                        name: item.ingredient_name,
                        unit: item.unit,
                        isSuggested: suggestedItems.includes(item.ingredient_name)
                    });
                }
            });

            // Önce önerilenler, sonra diğerleri
            const sorted = uniqueItems.sort((a, b) => (b.isSuggested ? 1 : 0) - (a.isSuggested ? 1 : 0));
            setInventory(sorted);
        } catch (err) {
            console.error('Failed to fetch breakfast inventory:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = (name, level) => {
        setSelections(prev => {
            if (prev[name] === level) {
                const next = { ...prev };
                delete next[name];
                return next;
            }
            return { ...prev, [name]: level };
        });
    };

    const handleSubmit = async () => {
        const itemsToDeduct = Object.entries(selections).map(([name, level]) => ({
            name,
            level
        }));

        if (itemsToDeduct.length === 0) return;

        setIsSubmitting(true);
        try {
            const authToken = localStorage.getItem('authToken');
            // Not: Bu endpoint Adım 4 kapsamında backend tarafında revize edilecek (Deduct Logic)
            const res = await fetch(`${API_BASE_URL}/api/inventory/deduct-bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ items: itemsToDeduct })
            });

            if (res.ok) {
                if (onComplete) onComplete();
            }
        } catch (err) {
            console.error('Deduction failed:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isLoading && inventory.length === 0) {
        return (
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-3xl p-8 text-center border border-slate-100 dark:border-slate-800">
                    <span className="material-symbols-outlined text-6xl text-slate-300 mb-4">inventory_2</span>
                    <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2 uppercase">GİRİŞ YAPILMAMIŞ</h3>
                    <p className="text-sm text-slate-500 mb-6 font-medium">Şefim, mutfağınızda kahvaltılık bir ürün tespit edemedik. Önce envantere ürün girmelisiniz.</p>
                    <button onClick={onClose} className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase text-xs tracking-widest">TAMAM</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-scale-up border border-slate-100 dark:border-slate-800">
                {/* Header */}
                <div className="p-8 pb-4 flex items-center justify-between border-b border-slate-50 dark:border-slate-800">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                            <span className="material-symbols-outlined text-amber-500 text-3xl">breakfast_dining</span>
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-slate-800 dark:text-white leading-tight uppercase tracking-tight">Günlük Kahvaltı Takibi</h3>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Neler Yedik?</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-red-500 transition-all">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 space-y-6 no-scrollbar">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-800/20">
                        <p className="text-xs font-bold text-blue-600 dark:text-blue-400 leading-relaxed uppercase tracking-wide">
                            Şefim, bu sabah nelerden, ne kadar tükettiğimizi seçersen stokları senin yerine hemen güncelleyebilirim.
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="h-40 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {inventory.map((item) => (
                                <div key={item.name} className={`p-5 rounded-3xl border transition-all ${selections[item.name] ? 'bg-primary/5 border-primary shadow-lg shadow-primary/5' : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800'}`}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.isSuggested ? 'bg-amber-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                                            <span className="material-symbols-outlined text-xl">{item.isSuggested ? 'star' : 'shopping_basket'}</span>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-black text-slate-800 dark:text-white uppercase truncate max-w-[120px]">{item.name}</h4>
                                            {item.isSuggested && <span className="text-[9px] font-black text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded tracking-tighter uppercase">Akıllı Tahmin</span>}
                                        </div>
                                    </div>

                                    <div className="flex bg-white dark:bg-slate-900 rounded-2xl p-1 shadow-sm border border-slate-100 dark:border-slate-800">
                                        {['az', 'orta', 'cok'].map((l) => (
                                            <button
                                                key={l}
                                                onClick={() => handleSelect(item.name, l)}
                                                className={`flex-1 py-1.5 text-[10px] font-black uppercase rounded-xl transition-all ${selections[item.name] === l ? 'bg-primary text-white shadow-md' : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                            >
                                                {l === 'az' ? 'Az' : (l === 'orta' ? 'Orta' : 'Bol')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-8 pt-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-50 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 text-left">Seçilenler</span>
                            <span className="text-sm font-black text-slate-800 dark:text-white">{Object.keys(selections).length} Ürün</span>
                        </div>
                        <div className="h-10 w-px bg-slate-200 dark:bg-slate-700 mx-4"></div>

                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || Object.keys(selections).length === 0}
                        className="w-full py-5 bg-primary text-white rounded-3xl font-black uppercase text-sm tracking-[0.2em] shadow-2xl shadow-primary/40 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                    >
                        {isSubmitting ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <>
                                <span className="material-symbols-outlined">inventory</span>
                                MUTFAK MALZEMELERIMI GÜNCELLE
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
